import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Sparkles, Send } from "lucide-react";

// Gemini Training Coach — appears only in Training, only AFTER submission,
// below the human-reviewed sample reason. Never scores qualification.
export default function TrainingCoach({ itemId }) {
  const [turns, setTurns] = useState([]); // {role:'coach'|'you', text}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [capped, setCapped] = useState(false);
  const autoFired = useRef(null);

  const call = async (mode, message) => {
    setLoading(true);
    try {
      const r = await api.post("/eval/training/coach", { item_id: itemId, mode, message });
      const d = r.data || {};
      if (d.reply) {
        setTurns((t) => [...t, { role: "coach", text: d.reply }]);
      } else if (d.capped) {
        setTurns((t) => [...t, { role: "system", text: "You've reached the training coach limit for this session. Training continues with the human-reviewed sample and feedback." }]);
      } else if (d.error === "unavailable") {
        setTurns((t) => [...t, { role: "system", text: "The coach is briefly unavailable — that's fine, keep going. Your training is not affected." }]);
      }
      if (typeof d.remaining === "number") setRemaining(d.remaining);
      if (d.capped) setCapped(true);
    } catch (e) {
      setTurns((t) => [...t, { role: "system", text: "The coach is briefly unavailable — that's fine, keep going." }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFired.current === itemId) return;
    autoFired.current = itemId;
    setTurns([]); setInput(""); setCapped(false);
    call("auto", "");
    // eslint-disable-next-line
  }, [itemId]);

  const send = () => {
    const q = input.trim();
    if (!q || loading || capped) return;
    setTurns((t) => [...t, { role: "you", text: q }]);
    setInput("");
    call("followup", q);
  };

  return (
    <div className="mt-6 border border-[#C79447]/40 bg-[#0A0A0A] text-[#F6F3EC] rounded-sm p-5" data-testid="training-coach">
      <div className="flex items-center gap-2 text-[#C79447]">
        <Sparkles className="h-4 w-4" />
        <span className="eyebrow">Training Coach</span>
      </div>
      <div className="mt-4 space-y-3" data-testid="coach-thread">
        {turns.map((t, i) => (
          <div key={i} className={t.role === "you" ? "text-right" : ""}>
            <p className={`inline-block text-sm leading-relaxed rounded-sm px-3 py-2 max-w-[90%] whitespace-pre-wrap ${t.role === "you" ? "bg-[#C79447] text-[#0A0A0A]" : t.role === "system" ? "text-[#A3A3A3] italic" : "bg-white/5 text-[#E9E5DA]"}`}>
              {t.text}
            </p>
          </div>
        ))}
        {loading && <div className="flex items-center gap-2 text-sm text-[#A3A3A3]"><Loader2 className="h-4 w-4 animate-spin" /> Coach is thinking…</div>}
      </div>
      {!capped ? (
        <div className="mt-4 flex items-center gap-2">
          <input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            disabled={loading}
            placeholder="Ask about this item, C1–C4, or how to improve your rationale…"
            className="flex-1 bg-transparent border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F6F3EC] placeholder-[#71717A] focus:outline-none focus:border-[#C79447]"
          />
          <button data-testid="coach-send-btn" onClick={send} disabled={loading || !input.trim()} className="inline-flex items-center gap-1.5 bg-[#C79447] text-[#0A0A0A] px-3 py-2 rounded-sm text-sm font-medium disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p className="mt-4 text-xs text-[#A3A3A3]" data-testid="coach-capped">Training coach limit reached for this session.</p>
      )}
      {remaining !== null && !capped && <p className="mt-2 text-xs text-[#71717A]">Coach messages remaining this session: {remaining}</p>}
    </div>
  );
}
